import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { ShoppingCart, Plus, Minus, X, Bell, Receipt, Check, Banknote, CreditCard, Clock, Info, ChevronDown, ChevronUp, Filter, RotateCcw } from 'lucide-react';
import { Logo } from '../components/Logo';
import { displayCategoryEmoji } from '../lib/categoryEmoji';
import { formatCaloriesPer100g, formatCaloriesPerPortion } from '../lib/nutrition';
import { MenuLanguageFlagsBar } from '../components/MenuFlag';
import { ClientRestaurantInfoModal } from '../components/ClientRestaurantInfoModal';
import {
    createMenuT, getMenuStrings, persistClientMenuLang, resolveClientMenuLang,
} from '../lib/menuLocale';
import { localizeMenuCatalog } from '../lib/menuContentLocale';

// Live order tracker shown to the client at the top of the menu. Renders 5 steps
// (Trimisă → Confirmată → Se prepară → Gata → Servită) with the current one highlighted.
// Auto-updates via parent's polling — re-renders when `order` prop changes.
const ORDER_STEPS = [
    { key: 'PENDING', label: 'Trimisă', icon: '📨' },
    { key: 'CONFIRMED', label: 'Confirmată', icon: '✓' },
    { key: 'IN_KITCHEN', label: 'Se prepară', icon: '🍳' },
    { key: 'READY', label: 'Gata', icon: '🔔' },
    { key: 'SERVED', label: 'Servită', icon: '🍽️' },
];

// Per-item status badge — derived from the same fields the backend uses.
// Returns { label, color, dim } for visual rendering inside the expanded order list.
function itemStatusBadge(item, orderStatus) {
    if (item.is_extra && item.source === 'client') {
        const es = item.extra_state || 'new';
        if (es === 'new') return { label: '⏳ Așteaptă confirmare', color: 'var(--amber)' };
        if (es === 'sent') return { label: '🍳 La bucătărie', color: 'var(--neon-cyan)' };
        if (es === 'cooking') return { label: '🔥 Se prepară', color: 'var(--neon-cyan)' };
        if (es === 'ready') return { label: '🔔 Gata de servit', color: 'var(--amber)' };
        if (es === 'served') return { label: '✓ Servit', color: 'var(--neon-teal)', dim: true };
    }
    // Regular (non-extra) items — derive from serve_state + global status
    const ss = item.serve_state;
    if (orderStatus === 'PAID' || ss === 'served') return { label: '✓ Servit', color: 'var(--neon-teal)', dim: true };
    if (ss === 'ready') return { label: '🔔 Gata de servit', color: 'var(--amber)' };
    if (ss === 'cooking') return { label: '🔥 Se prepară', color: 'var(--neon-cyan)' };
    if (ss === 'pending' && orderStatus === 'PENDING') return { label: '⏳ Așteaptă confirmare', color: 'var(--text-muted)' };
    if (ss === 'pending') return { label: '🍳 La bucătărie', color: 'var(--neon-cyan)' };
    return { label: '', color: 'var(--text-muted)' };
}


function ClientOrderStatus({ order, currency }) {
    const [expanded, setExpanded] = useState(false);
    if (!order) return null;
    const status = order.status;
    if (status === 'CANCELLED') {
        return (
            <div className="client-active-order-card" data-testid="active-order-banner" style={{ borderColor: 'var(--neon-magenta)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>❌</span>
                    <strong>Comandă anulată</strong>
                </div>
            </div>
        );
    }
    if (status === 'PAID') {
        return (
            <div className="client-active-order-card" data-testid="active-order-banner" style={{ borderColor: 'var(--neon-teal)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Check size={16} color="var(--neon-teal)" />
                    <strong>Plătită · Mulțumim!</strong>
                    <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        {order.total?.toFixed(2)} {currency}
                    </span>
                </div>
            </div>
        );
    }
    const currentIdx = Math.max(0, ORDER_STEPS.findIndex((s) => s.key === status));
    const items = order.items || [];
    const itemCount = items.reduce((s, it) => s + (Number(it.quantity) || 1), 0);
    return (
        <div className="client-active-order-card" data-testid="active-order-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <span className="live-dot" />
                <strong style={{ fontSize: '0.95rem' }}>Comanda #{order.id.slice(0, 6)}</strong>
                <span style={{ marginLeft: 'auto', color: 'var(--amber)', fontWeight: 600 }}>
                    {order.total?.toFixed(2)} {currency}
                </span>
            </div>
            <div className="client-order-steps">
                {ORDER_STEPS.map((step, i) => {
                    const isDone = i < currentIdx;
                    const isActive = i === currentIdx;
                    const isFuture = i > currentIdx;
                    return (
                        <React.Fragment key={step.key}>
                            <div className={`client-order-step${isActive ? ' client-order-step--active' : ''}${isDone ? ' client-order-step--done' : ''}${isFuture ? ' client-order-step--future' : ''}`}
                                 title={step.label}>
                                <div className="client-order-step__dot">
                                    {isDone ? <Check size={12} /> : <span>{step.icon}</span>}
                                </div>
                                <div className="client-order-step__label">{step.label}</div>
                            </div>
                            {i < ORDER_STEPS.length - 1 && (
                                <div className={`client-order-step__connector${i < currentIdx ? ' client-order-step__connector--done' : ''}`} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            <div style={{ marginTop: 8, fontSize: '0.82rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                {status === 'PENDING' && '👀 Ospătarul îți confirmă comanda.'}
                {status === 'CONFIRMED' && '👨‍🍳 Comanda merge la bucătărie.'}
                {status === 'IN_KITCHEN' && '🔥 Bucătăria pregătește comanda…'}
                {status === 'READY' && '🍽️ Gata! Ospătarul vine cu mâncarea.'}
                {status === 'SERVED' && '😋 Poftă bună! Cere nota când dorești.'}
            </div>

            {items.length > 0 && (
                <>
                    <button type="button"
                            className="client-order-toggle"
                            onClick={() => setExpanded((v) => !v)}
                            aria-expanded={expanded}
                            data-testid="active-order-toggle">
                        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        <span>{expanded ? 'Ascunde detaliile' : `Vezi cele ${itemCount} produse comandate`}</span>
                    </button>

                    <div className={`client-order-items${expanded ? ' client-order-items--open' : ''}`}
                         data-testid="active-order-items">
                        <div className="client-order-items__inner">
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                                Produse comandate
                            </div>
                            {items.map((it, i) => {
                                const badge = itemStatusBadge(it, status);
                                const dim = badge.dim;
                                const lineTotal = (Number(it.price) || 0) * (Number(it.quantity) || 1);
                                return (
                                    <div key={i} className="client-order-item" style={{ opacity: dim ? 0.6 : 1 }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                                                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{it.quantity}×</span>
                                                <span style={{ fontSize: '0.88rem' }}>
                                                    {it.product_name}
                                                    {it.variant_name ? <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}> · {it.variant_name}</span> : null}
                                                </span>
                                            </div>
                                            {it.notes && (
                                                <div style={{ fontSize: '0.75rem', color: 'var(--amber)', marginTop: 2 }}>
                                                    💬 {it.notes}
                                                </div>
                                            )}
                                            {badge.label && (
                                                <div style={{ fontSize: '0.72rem', color: badge.color, marginTop: 3, fontWeight: 500 }}>
                                                    {badge.label}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--amber)', flexShrink: 0 }}>
                                            {lineTotal.toFixed(2)}
                                        </div>
                                    </div>
                                );
                            })}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', fontSize: '0.92rem' }}>
                                <strong>Total</strong>
                                <strong style={{ color: 'var(--amber)' }}>{order.total?.toFixed(2)} {currency}</strong>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}


function ProductAllergens({ allergens, label }) {
    if (!allergens?.length) return null;
    return (
        <div style={{ marginTop: 8 }} data-testid="product-allergens">
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                {label}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {allergens.map((a) => (
                    <span key={a} className="badge badge-magenta" style={{ fontSize: '0.62rem' }}>{a}</span>
                ))}
            </div>
        </div>
    );
}

function normalizeTagList(tags) {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags.map((t) => String(t).trim()).filter(Boolean);
    if (typeof tags === 'string') return tags.split(',').map((s) => s.trim()).filter(Boolean);
    return [];
}

function ProductDescription({ description, label }) {
    if (!description?.trim()) return null;
    return (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: 4, lineHeight: 1.45 }} data-testid="product-description">
            <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{label}: </span>
            {description}
        </div>
    );
}

function ProductTags({ tags, label }) {
    const items = normalizeTagList(tags);
    if (!items.length) return null;
    return (
        <div style={{ marginTop: 8 }} data-testid="product-tags">
            <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginBottom: 4, lineHeight: 1.45 }}>
                <span style={{ fontWeight: 600 }}>{label}:</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {items.map((tag) => (
                    <span key={tag} className="badge badge-teal" style={{ fontSize: '0.62rem' }}>{tag}</span>
                ))}
            </div>
        </div>
    );
}

function ProductIngredients({ ingredients, label }) {
    const items = normalizeTagList(ingredients);
    if (!items.length) return null;
    return (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: 4, lineHeight: 1.45 }} data-testid="product-ingredients">
            <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{label}: </span>
            {items.join(', ')}
        </div>
    );
}

const NUTRI_LABELS_PUBLIC = [
    ['kj', 'kJ'],
    ['kcal', 'kcal'],
    ['fat_g', null],
    ['saturated_g', null],
    ['carbs_g', null],
    ['sugars_g', null],
    ['protein_g', null],
    ['salt_g', null],
];

function parseGramsFromWeight(weight) {
    if (weight == null) return null;
    const s = String(weight).toLowerCase().replace(',', '.');
    const m = s.match(/(\d+(?:\.\d+)?)\s*(kg|g|ml|l)?/);
    if (!m) return null;
    const n = parseFloat(m[1]);
    if (Number.isNaN(n)) return null;
    const unit = m[2] || 'g';
    if (unit === 'kg' || unit === 'l') return n * 1000;
    return n;
}

function formatNutriRow(nutrition, factor) {
    const round = (v, d = 1) => {
        if (v == null) return null;
        const n = v * factor;
        const p = Math.pow(10, d);
        return Math.round(n * p) / p;
    };
    const parts = [
        nutrition.kcal != null && `${Math.round(nutrition.kcal * factor)} kcal`,
        nutrition.fat_g != null && `G ${round(nutrition.fat_g, 1)}g`,
        nutrition.carbs_g != null && `C ${round(nutrition.carbs_g, 1)}g`,
        nutrition.protein_g != null && `P ${round(nutrition.protein_g, 1)}g`,
        nutrition.salt_g != null && `Sare ${round(nutrition.salt_g, 2)}g`,
    ];
    return parts.filter(Boolean).join(' · ');
}

function ProductNutrition({ nutrition, variantWeight }) {
    if (!nutrition || nutrition.kcal == null) return null;
    const isPortion = nutrition.basis === 'portion' && nutrition.portion_grams;
    const portionGrams = isPortion
        ? nutrition.portion_grams
        : (nutrition.portion_grams || parseGramsFromWeight(variantWeight));
    const rows = [];
    if (isPortion) {
        rows.push({ label: `per ${portionGrams} g (porție)`, text: formatNutriRow(nutrition, 1) });
        // Derive per 100g back from the portion declaration.
        if (portionGrams && portionGrams !== 100) {
            const factor100 = 100 / portionGrams;
            rows.push({ label: 'per 100 g', text: formatNutriRow(nutrition, factor100) });
        }
    } else {
        rows.push({ label: 'per 100 g', text: formatNutriRow(nutrition, 1) });
        if (portionGrams && portionGrams !== 100) {
            const factorPortion = portionGrams / 100;
            rows.push({ label: `per ${portionGrams} g (porție)`, text: formatNutriRow(nutrition, factorPortion) });
        }
    }
    return (
        <div
            data-testid="product-nutrition"
            style={{
                marginTop: 4,
                color: 'var(--text-faint)',
                fontSize: '0.72rem',
                lineHeight: 1.4,
                fontVariantNumeric: 'tabular-nums',
            }}>
            {rows.map((r, i) => (
                <div key={i}>
                    {r.text} <span style={{ opacity: 0.7 }}>· {r.label}</span>
                </div>
            ))}
        </div>
    );
}

// Diet preferences shown as "show ONLY" chips — must ALL match a product's tags.
// Each entry lists multiple labels (RO + EN + translated forms) so matching works
// regardless of menu language. Match is case-insensitive on the raw product.tags.
const DIET_PRESETS = [
    { id: 'vegetarian', label: 'Vegetarian', icon: '🥗', matches: ['vegetarian'] },
    { id: 'vegan', label: 'Vegan', icon: '🌱', matches: ['vegan'] },
    { id: 'fara_gluten', label: 'Fără gluten', icon: '🚫', matches: ['fără gluten', 'fara gluten', 'gluten free'] },
    { id: 'halal', label: 'Halal', icon: '🕌', matches: ['halal'] },
    { id: 'kosher', label: 'Kosher', icon: '✡️', matches: ['kosher'] },
    { id: 'light', label: 'Light', icon: '🪶', matches: ['light'] },
];

// The 14 EU mandatory allergens (Annex II of Regulation 1169/2011). Hidden
// products are those whose allergens list contains ANY of the selected items.
// `aliases` covers RO/EN labels and legacy IDs already stored in the DB.
const ALLERGEN_PRESETS = [
    { id: 'gluten',     label: 'Gluten',          icon: '🌾',  aliases: ['gluten'] },
    { id: 'lapte',      label: 'Lapte / lactoză', icon: '🥛',  aliases: ['lapte', 'lactoză', 'lactoza', 'milk', 'dairy'] },
    { id: 'oua',        label: 'Ouă',             icon: '🥚',  aliases: ['ouă', 'oua', 'egg', 'eggs'] },
    { id: 'peste',      label: 'Pește',           icon: '🐟',  aliases: ['pește', 'peste', 'fish'] },
    { id: 'crustacee',  label: 'Crustacee',       icon: '🦐',  aliases: ['crustacee', 'crustaceans', 'shellfish'] },
    { id: 'moluste',    label: 'Moluște',         icon: '🦪',  aliases: ['moluște', 'moluste', 'molluscs', 'mollusks'] },
    { id: 'arahide',    label: 'Arahide',         icon: '🥜',  aliases: ['arahide', 'peanuts'] },
    { id: 'fructe_coaja', label: 'Fructe cu coajă', icon: '🌰', aliases: ['fructe cu coajă', 'fructe cu coaja', 'fructe_coaja', 'nuts', 'tree nuts'] },
    { id: 'soia',       label: 'Soia',            icon: '🫘',  aliases: ['soia', 'soy', 'soya'] },
    { id: 'susan',      label: 'Susan',           icon: '🌱',  aliases: ['susan', 'sesame'] },
    { id: 'telina',     label: 'Țelină',          icon: '🥬',  aliases: ['țelină', 'telina', 'celery'] },
    { id: 'mustar',     label: 'Muștar',          icon: '🌭',  aliases: ['muștar', 'mustar', 'mustard'] },
    { id: 'sulfiti',    label: 'Sulfiți',         icon: '🧪',  aliases: ['sulfiți', 'sulfiti', 'sulphites', 'sulfites'] },
    { id: 'lupin',      label: 'Lupin',           icon: '🌿',  aliases: ['lupin'] },
];

const ALLERGEN_ID_BY_ALIAS = (() => {
    const m = {};
    ALLERGEN_PRESETS.forEach((a) => a.aliases.forEach((al) => { m[al.toLowerCase()] = a.id; }));
    return m;
})();

function productAllergenIds(product) {
    const out = new Set();
    (product?.allergens || []).forEach((raw) => {
        const key = String(raw || '').trim().toLowerCase();
        const id = ALLERGEN_ID_BY_ALIAS[key];
        if (id) out.add(id);
    });
    return out;
}

function productMatchesDiet(product, dietId) {
    const diet = DIET_PRESETS.find((d) => d.id === dietId);
    if (!diet) return true;
    const tags = (product?.tags || []).map((t) => String(t).trim().toLowerCase());
    return diet.matches.some((m) => tags.includes(m.toLowerCase()));
}

function passesMenuFilters(product, hiddenAllergens, requiredDiets) {
    // If any required diet isn't satisfied, hide the product.
    for (const dietId of requiredDiets) {
        if (!productMatchesDiet(product, dietId)) return false;
    }
    if (!hiddenAllergens.size) return true;
    const pAll = productAllergenIds(product);
    for (const aid of hiddenAllergens) {
        if (pAll.has(aid)) return false;
    }
    return true;
}

function MenuFiltersModal({ open, onClose, hiddenAllergens, requiredDiets, onChange, hiddenCount, totalCount }) {
    if (!open) return null;
    const toggleAllergen = (id) => {
        const next = new Set(hiddenAllergens);
        if (next.has(id)) next.delete(id); else next.add(id);
        onChange({ hiddenAllergens: next, requiredDiets });
    };
    const toggleDiet = (id) => {
        const next = new Set(requiredDiets);
        if (next.has(id)) next.delete(id); else next.add(id);
        onChange({ hiddenAllergens, requiredDiets: next });
    };
    const reset = () => onChange({ hiddenAllergens: new Set(), requiredDiets: new Set() });
    const anyActive = hiddenAllergens.size > 0 || requiredDiets.size > 0;

    const chipBase = {
        padding: '0.4rem 0.65rem',
        borderRadius: 999,
        border: '1px solid var(--border)',
        background: 'var(--surface-2)',
        color: 'var(--text-muted)',
        fontSize: '0.82rem',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        whiteSpace: 'nowrap',
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h2 className="headline" style={{ fontSize: '1.5rem', margin: 0 }}>Filtre meniu</h2>
                    <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Închide"><X size={16} /></button>
                </div>

                <div className="kicker" style={{ marginBottom: 8 }}>Arată DOAR produse:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                    {DIET_PRESETS.map((d) => {
                        const active = requiredDiets.has(d.id);
                        return (
                            <button
                                key={d.id}
                                type="button"
                                onClick={() => toggleDiet(d.id)}
                                data-testid={`filter-diet-${d.id}`}
                                style={{
                                    ...chipBase,
                                    border: `1px solid ${active ? 'var(--neon-teal)' : 'var(--border)'}`,
                                    background: active ? 'rgba(45,212,191,0.14)' : 'var(--surface-2)',
                                    color: active ? 'var(--neon-teal)' : 'var(--text-muted)',
                                    fontWeight: active ? 600 : 500,
                                }}
                            >
                                <span>{d.icon}</span><span>{d.label}</span>
                                {active && <Check size={11} />}
                            </button>
                        );
                    })}
                </div>

                <div className="kicker" style={{ marginBottom: 8 }}>Ascunde produse care conțin:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                    {ALLERGEN_PRESETS.map((a) => {
                        const active = hiddenAllergens.has(a.id);
                        return (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => toggleAllergen(a.id)}
                                data-testid={`filter-allergen-${a.id}`}
                                style={{
                                    ...chipBase,
                                    border: `1px solid ${active ? 'var(--amber)' : 'var(--border)'}`,
                                    background: active ? 'rgba(242,101,34,0.16)' : 'var(--surface-2)',
                                    color: active ? 'var(--amber)' : 'var(--text-muted)',
                                    fontWeight: active ? 600 : 500,
                                }}
                            >
                                <span>{a.icon}</span><span>{a.label}</span>
                                {active && <X size={11} />}
                            </button>
                        );
                    })}
                </div>

                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                    marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--border)',
                }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        {anyActive
                            ? <>Afișate <strong style={{ color: 'var(--text)' }}>{totalCount - hiddenCount}</strong> din <strong style={{ color: 'var(--text)' }}>{totalCount}</strong></>
                            : <>Nicio restricție aplicată</>}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {anyActive && (
                            <button type="button" className="btn btn-secondary btn-sm" onClick={reset} data-testid="filter-reset">
                                <RotateCcw size={13} /> Resetează
                            </button>
                        )}
                        <button type="button" className="btn btn-primary btn-sm" onClick={onClose} data-testid="filter-close">
                            Vezi meniul
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function ClientMenu() {
    const { slug, table_token } = useParams();
    const [searchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [cart, setCart] = useState([]);
    const [openCart, setOpenCart] = useState(false);
    const [openProduct, setOpenProduct] = useState(null);
    const [submitted, setSubmitted] = useState(null);
    const [openBill, setOpenBill] = useState(false);
    const [billSent, setBillSent] = useState(false);
    const [activeCat, setActiveCat] = useState(null);
    const [lang, setLang] = useState('ro');
    const [openInfo, setOpenInfo] = useState(false);
    const [openFilters, setOpenFilters] = useState(false);
    const [hiddenAllergens, setHiddenAllergens] = useState(new Set());
    const [requiredDiets, setRequiredDiets] = useState(new Set());
    const [langChanging, setLangChanging] = useState(false);

    const isPreview = table_token === 'demo-preview';
    const [activeOrder, setActiveOrder] = useState(null);

    const enabledLangs = data?.restaurant?.menu_languages?.enabled || ['ro', 'en'];
    const currency = data?.restaurant?.currency || 'RON';
    const t = useMemo(() => createMenuT(lang), [lang]);
    const calTpl = useMemo(() => getMenuStrings(lang).caloriesPer100g, [lang]);
    const calPortionTpl = useMemo(() => getMenuStrings(lang).caloriesPerPortion || '{{n}} kcal / porție', [lang]);
    const fmtCal = (calories) => formatCaloriesPer100g(calories, calTpl);
    const fmtCalPortion = (calories) => formatCaloriesPerPortion(calories, calPortionTpl);
    const menuCatalog = useMemo(
        () => localizeMenuCatalog(data?.categories, data?.products, lang),
        [data?.categories, data?.products, lang],
    );

    // Persist diet/allergen filters per restaurant slug so a returning client doesn't
    // have to re-tick "fără gluten" on every scan.
    const filtersStorageKey = `menu_filters_${slug}`;
    useEffect(() => {
        try {
            const raw = localStorage.getItem(filtersStorageKey);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed.hiddenAllergens)) setHiddenAllergens(new Set(parsed.hiddenAllergens));
            if (Array.isArray(parsed.requiredDiets)) setRequiredDiets(new Set(parsed.requiredDiets));
        } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const updateFilters = useCallback(({ hiddenAllergens: ha, requiredDiets: rd }) => {
        setHiddenAllergens(ha);
        setRequiredDiets(rd);
        try {
            localStorage.setItem(filtersStorageKey, JSON.stringify({
                hiddenAllergens: Array.from(ha),
                requiredDiets: Array.from(rd),
            }));
        } catch {}
    }, [filtersStorageKey]);

    // Filter set computed against the ORIGINAL (untranslated) products so allergen /
    // tag labels match consistently regardless of the selected menu language.
    const filteredProductIds = useMemo(() => {
        const out = new Set();
        (data?.products || []).forEach((p) => {
            if (passesMenuFilters(p, hiddenAllergens, requiredDiets)) out.add(p.id);
        });
        return out;
    }, [data?.products, hiddenAllergens, requiredDiets]);

    const filtersActiveCount = hiddenAllergens.size + requiredDiets.size;
    // Filter products to whatever's currently active — main or subcategory.
    // Two-row nav model: the second row exposes subcategories of the selected
    // main, so users always pick exactly one category to view at a time.
    const totalProductsInCategory = useMemo(() => {
        if (!activeCat) return 0;
        return (data?.products || []).filter((p) => p.category_id === activeCat).length;
    }, [data?.products, activeCat]);
    const hiddenInCategory = useMemo(() => {
        if (!filtersActiveCount || !activeCat) return 0;
        return (data?.products || []).filter((p) =>
            p.category_id === activeCat && !filteredProductIds.has(p.id)
        ).length;
    }, [data?.products, activeCat, filteredProductIds, filtersActiveCount]);

    const loadMenu = useCallback(async (nextLang) => {
        const response = await api.get(`/c/menu/${slug}/${table_token}`, { params: { lang: nextLang }, timeout: 20000 });
        const payload = response.data;
        const enabled = payload.restaurant?.menu_languages?.enabled || ['ro', 'en'];
        const resolved = payload.lang || resolveClientMenuLang({
            slug, enabled, urlLang: searchParams.get('lang'),
        });
        setLang(resolved);
        persistClientMenuLang(slug, resolved);
        setData(payload);
        const firstMain = (payload.categories || []).find(c => !c.parent_id) || payload.categories?.[0];
        if (firstMain) setActiveCat(firstMain.id);
        return payload;
    }, [slug, table_token, searchParams]);


    useEffect(() => {
        const urlLang = searchParams.get('lang');
        const initial = resolveClientMenuLang({ slug, enabled: ['ro', 'en'], urlLang });
        setLang(initial);
        loadMenu(initial).catch((e) => setError(formatApiError(e)));
        if (!isPreview) {
            api.get(`/c/orders/active/${table_token}`).then((r) => setActiveOrder(r.data.order)).catch(() => {});
        }
    }, [slug, table_token, isPreview, searchParams, loadMenu]);

    const refreshActiveOrder = () => {
        if (isPreview) return;
        api.get(`/c/orders/active/${table_token}`).then((r) => setActiveOrder(r.data.order)).catch(() => setActiveOrder(null));
    };
    useEffect(() => { refreshActiveOrder(); }, [table_token, isPreview]); // eslint-disable-line

    // Live order tracking: poll every 8s while the order is in flight. Stops when the
    // order is closed (PAID/CANCELLED) or no active order exists. Cheaper than a WS
    // connection from an unauth endpoint and good enough UX (status doesn't change often).
    useEffect(() => {
        if (isPreview) return;
        const inFlight = activeOrder && !['PAID', 'CANCELLED'].includes(activeOrder.status);
        // Always poll lightly even without an active order, so a freshly-placed order shows up.
        const intervalMs = inFlight ? 8000 : 15000;
        const id = setInterval(refreshActiveOrder, intervalMs);
        return () => clearInterval(id);
    }, [activeOrder?.id, activeOrder?.status, isPreview]); // eslint-disable-line

    const changeLang = async (nextLang) => {
        persistClientMenuLang(slug, nextLang);
        setOpenProduct(null);
        setLangChanging(true);
        try {
            await loadMenu(nextLang);
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setLangChanging(false);
        }
    };

    const addToCart = (product, variant) => {
        if (!product?.id || !variant?.id) {
            alert('Produsul nu poate fi adăugat. Reîncarcă meniul și încearcă din nou.');
            return;
        }
        setCart((prev) => {
            const idx = prev.findIndex((i) => i.product_id === product.id && i.variant_id === variant.id);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 };
                return copy;
            }
            return [...prev, {
                product_id: String(product.id),
                variant_id: String(variant.id),
                product_name: String(product.name || 'Produs'),
                variant_name: String(variant.name || 'Porție'),
                price: Number(variant.price) || 0,
                quantity: 1,
                notes: '',
            }];
        });
        setOpenProduct(null);
    };

    const changeQty = (i, d) => {
        setCart((prev) => {
            const copy = [...prev];
            copy[i] = { ...copy[i], quantity: Math.max(0, copy[i].quantity + d) };
            return copy.filter((x) => x.quantity > 0);
        });
    };

    const total = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
    const isQrOpen = () => data?.open_status?.is_open !== false;

    const buildOrderItems = () => cart.map((item) => ({
        product_id: String(item.product_id || ''),
        variant_id: String(item.variant_id || ''),
        product_name: String(item.product_name || 'Produs').trim() || 'Produs',
        variant_name: String(item.variant_name || 'Porție').trim() || 'Porție',
        price: Number(item.price) || 0,
        quantity: Math.max(1, Number(item.quantity) || 1),
        notes: item.notes ? String(item.notes) : '',
    })).filter((item) => item.product_id && item.variant_id);

    const sendOrder = async () => {
        if (!isQrOpen()) return;
        const items = buildOrderItems();
        if (!items.length) {
            alert('Coșul conține produse invalide. Reîncarcă meniul și adaugă din nou.');
            return;
        }
        try {
            let order;
            if (activeOrder) {
                const { data: resp } = await api.post(`/c/orders/${activeOrder.id}/items`, {
                    table_token, items, notes: '',
                });
                order = resp;
            } else {
                const { data: resp } = await api.post('/c/orders', {
                    table_token, items, notes: '',
                });
                order = resp;
            }
            setSubmitted(order);
            setCart([]);
            setOpenCart(false);
            refreshActiveOrder();
        } catch (e) { alert(formatApiError(e)); }
    };

    const callWaiter = async () => {
        if (!isQrOpen()) return;
        try {
            await api.post('/c/action', { table_token, type: 'call_waiter' });
            alert(t('waiterCalled'));
        } catch (e) { alert(formatApiError(e)); }
    };

    const requestBill = async (paymentMethod) => {
        if (!isQrOpen()) return;
        try {
            await api.post('/c/action', { table_token, type: 'bill_request', payment_method: paymentMethod });
            setOpenBill(false);
            setBillSent(true);
        } catch (e) { alert(formatApiError(e)); }
    };

    if (error) return (
        <div style={{ padding: '4rem 1.5rem', textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
            <h1 className="headline" style={{ fontSize: '2rem' }}>{t('errorTitle')}</h1>
            <p style={{ color: 'var(--text-muted)' }}>{error || t('previewError')}</p>
        </div>
    );
    if (!data) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

    const { restaurant, table, open_status: openStatus } = data;
    const { categories, products } = menuCatalog;
    const isOpen = openStatus?.is_open !== false;
    const orderingEnabled = data?.ordering_enabled !== false;
    const callWaiterEnabled = data?.call_waiter_enabled !== false;
    const requestBillEnabled = data?.request_bill_enabled !== false;
    const showActionBar = orderingEnabled || callWaiterEnabled || requestBillEnabled;
    // Top nav: only main categories. Subcategories appear in a second row
    // underneath when the active main has any.
    const navCategories = categories.filter((c) => !c.parent_id);
    const subsByParent = categories.reduce((acc, c) => {
        if (c.parent_id) {
            (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
        }
        return acc;
    }, {});
    const activeCategoryObj = categories.find((c) => c.id === activeCat);
    const activeMainId = activeCategoryObj?.parent_id || activeCategoryObj?.id || null;
    const activeMainSubs = activeMainId ? (subsByParent[activeMainId] || []) : [];
    const activeMainHasDirectProducts = activeMainId
        ? products.some((p) => p.category_id === activeMainId)
        : false;
    const filtered = products.filter((p) =>
        p.category_id === activeCat && filteredProductIds.has(p.id),
    );

    // Clicking a main in the top nav: if the main has subs but no direct
    // products of its own, drop straight into the first sub so the user
    // doesn't land on an empty list.
    const handleMainCategoryClick = (catId) => {
        const subs = subsByParent[catId] || [];
        const hasDirect = products.some((p) => p.category_id === catId);
        if (subs.length && !hasDirect) {
            setActiveCat(subs[0].id);
        } else {
            setActiveCat(catId);
        }
    };

    const openProductModal = (p) => {
        if (!isOpen) return;
        setOpenProduct(p);
    };

    return (
        <div style={{ maxWidth: 480, margin: '0 auto', minHeight: '100vh', position: 'relative' }} data-testid="client-menu">
            {!isOpen && (
                <div style={{
                    margin: '0 1.2rem 0', padding: '1rem', borderRadius: 12,
                    background: 'rgba(255,46,146,0.08)', border: '1px solid rgba(255,46,146,0.35)',
                    display: 'flex', gap: 10, alignItems: 'flex-start',
                }} data-testid="closed-banner">
                    <Clock size={18} color="var(--neon-magenta)" style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('closedTitle')}</div>
                        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.88rem', lineHeight: 1.45 }}>
                            {openStatus?.message || t('closedFallback')}
                        </p>
                    </div>
                </div>
            )}
            <div style={{ position: 'relative', marginBottom: 0 }}>
                <div style={{
                    height: restaurant.cover_url ? 168 : 0,
                    background: restaurant.cover_url
                        ? `linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(10,10,10,0.55) 55%, var(--bg) 100%), url(${restaurant.cover_url}) center/cover no-repeat`
                        : 'transparent',
                }} data-testid="client-menu-cover" />
                <header style={{
                    padding: restaurant.cover_url ? '0 1.2rem 1.4rem' : '1.6rem 1.2rem',
                    marginTop: restaurant.cover_url ? -36 : 0,
                    borderBottom: '1px solid var(--border)',
                    position: 'relative',
                }} className={restaurant.cover_url ? '' : 'glass'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                        {restaurant.logo_url ? (
                            <img
                                src={restaurant.logo_url}
                                alt={restaurant.name}
                                style={{
                                    width: 52, height: 52, borderRadius: 14, objectFit: 'cover', flexShrink: 0,
                                    border: '2px solid rgba(255,255,255,0.15)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                                }}
                                data-testid="client-menu-logo"
                            />
                        ) : (
                            <Logo size="xs" to={null} showWordmark={false} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="kicker">{restaurant.name}</div>
                            <h1 className="headline" style={{ fontSize: '1.45rem', margin: 0, lineHeight: 1.2 }}>
                                {t('table')} <span style={{ color: 'var(--amber)' }}>{table.number}</span>
                            </h1>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm client-info-btn"
                                onClick={() => setOpenInfo(true)}
                                aria-label={t('detailsTitle')}
                                data-testid="client-info-open"
                            >
                                <Info size={16} />
                            </button>
                        </div>
                    </div>
                    {enabledLangs.length > 1 && (
                        <MenuLanguageFlagsBar
                            enabled={enabledLangs}
                            value={lang}
                            onChange={changeLang}
                            label={t('language')}
                            changing={langChanging}
                        />
                    )}
                    {!restaurant.cover_url && (
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: '0 0 0.4rem' }}>
                            {t('welcome')}
                        </p>
                    )}
                    {openStatus?.enabled && openStatus?.today_hours && (
                        <div style={{ marginTop: 8, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                            {t('hoursToday')}: {openStatus.today_hours}
                        </div>
                    )}
                </header>
            </div>

            <div className="client-menu-sticky">
                {activeOrder && !isPreview && (
                    <ClientOrderStatus order={activeOrder} currency={currency} />
                )}
                <div className="client-menu-cats">
                    <button
                        type="button"
                        onClick={() => setOpenFilters(true)}
                        className={`btn btn-sm ${filtersActiveCount > 0 ? 'btn-primary' : 'btn-secondary'}`}
                        data-testid="filter-open"
                        style={{ whiteSpace: 'nowrap', position: 'relative', flexShrink: 0 }}
                        title="Filtre alergeni & regim alimentar"
                    >
                        <Filter size={14} />
                        {filtersActiveCount > 0 && (
                            <span style={{
                                marginLeft: 4,
                                background: 'rgba(0,0,0,0.25)',
                                borderRadius: 999,
                                padding: '0 6px',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                            }}>{filtersActiveCount}</span>
                        )}
                    </button>
                    {navCategories.map((c) => {
                        // Highlight the main when EITHER the main itself is active OR
                        // one of its subcategories is — gives users a stable "where am I"
                        // anchor as they tap through subs in the row below.
                        const isActiveMain = activeMainId === c.id;
                        return (
                            <button key={c.id} onClick={() => handleMainCategoryClick(c.id)}
                                    className={`btn btn-sm ${isActiveMain ? 'btn-primary' : 'btn-secondary'}`}
                                    data-testid={`cat-tab-${c.id}`} style={{ whiteSpace: 'nowrap' }}>
                                {displayCategoryEmoji(c)} {c.name}
                            </button>
                        );
                    })}
                </div>
                {activeMainSubs.length > 0 && (
                    <div className="client-menu-cats client-menu-cats--subs" data-testid="client-menu-subnav">
                        {activeMainHasDirectProducts && (
                            <button
                                type="button"
                                onClick={() => setActiveCat(activeMainId)}
                                className={`btn btn-sm ${activeCat === activeMainId ? 'btn-primary' : 'btn-secondary'}`}
                                style={{ whiteSpace: 'nowrap' }}
                                data-testid={`subcat-tab-direct-${activeMainId}`}
                            >
                                ★ Toate
                            </button>
                        )}
                        {activeMainSubs.map((sub) => (
                            <button
                                key={sub.id}
                                type="button"
                                onClick={() => setActiveCat(sub.id)}
                                className={`btn btn-sm ${activeCat === sub.id ? 'btn-primary' : 'btn-secondary'}`}
                                style={{ whiteSpace: 'nowrap' }}
                                data-testid={`subcat-tab-${sub.id}`}
                            >
                                {displayCategoryEmoji(sub)} {sub.name}
                            </button>
                        ))}
                    </div>
                )}
                {filtersActiveCount > 0 && hiddenInCategory > 0 && (
                    <div style={{
                        padding: '0.45rem 1.2rem 0',
                        fontSize: '0.78rem',
                        color: 'var(--text-faint)',
                        textAlign: 'center',
                    }} data-testid="filter-hidden-hint">
                        <strong style={{ color: 'var(--amber)' }}>{hiddenInCategory}</strong> {hiddenInCategory === 1 ? 'produs ascuns' : 'produse ascunse'} de filtru ({totalProductsInCategory - hiddenInCategory} afișate)
                    </div>
                )}
            </div>

            <div style={{ padding: showActionBar ? '1rem 1.2rem 6rem' : '1rem 1.2rem 2rem' }}>
                {filtered.map((p) => (
                    <div key={p.id} className="panel panel-hover" style={{ marginBottom: '0.7rem', cursor: isOpen ? 'pointer' : 'not-allowed', opacity: isOpen ? 1 : 0.72 }}
                         onClick={() => openProductModal(p)} data-testid={`menu-product-${p.id}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
                            {p.image_url && (
                                <img src={p.image_url} alt={p.name} style={{ width: 72, height: 72, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                            )}
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{p.name}</div>
                                <ProductDescription description={p.description} label={t('description')} />
                                <ProductIngredients ingredients={p.ingredients} label={t('ingredients')} />
                                {(p.variants[0]?.weight || p.variants[0]?.calories || p.variants[0]?.calories_per_portion) && (
                                    <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 4 }}>
                                        {[
                                            p.variants[0].weight,
                                            fmtCal(p.variants[0].calories),
                                            fmtCalPortion(p.variants[0].calories_per_portion),
                                        ].filter(Boolean).join(' · ')}
                                    </div>
                                )}
                                <ProductNutrition nutrition={p.nutrition} variantWeight={p.variants[0]?.weight} />
                                <ProductTags tags={p.tags} label={t('tags')} />
                                <ProductAllergens allergens={p.allergens} label={t('allergens')} />
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div className="headline" style={{ fontSize: '1.3rem', color: 'var(--amber)' }}>
                                    {p.variants[0].price} <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{currency}</span>
                                </div>
                                {p.variants.length > 1 && <div className="kicker" style={{ fontSize: '0.6rem' }}>{t('fromPrice')}</div>}
                            </div>
                        </div>
                    </div>
                ))}
                {!filtered.length && <div style={{ color: 'var(--text-muted)', textAlign: 'center' }}>{t('emptyCategory')}</div>}
            </div>

            {showActionBar && (
                <div style={{
                    position: 'fixed', bottom: 0, left: 0, right: 0,
                    maxWidth: 480, margin: '0 auto', padding: 12,
                    background: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(20px)',
                    borderTop: '1px solid var(--border)',
                    display: 'flex', flexDirection: 'column', gap: 8,
                    paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
                }}>
                    {/* Row 1 — secondary actions: Cheamă ospătar + Cere nota (with text labels).
                        Cere nota is disabled once the active order is PAID — nothing left to pay. */}
                    {(callWaiterEnabled || requestBillEnabled) && (() => {
                        const orderPaid = activeOrder?.status === 'PAID';
                        return (
                            <div style={{ display: 'flex', gap: 8 }}>
                                {callWaiterEnabled && (
                                    <button className="btn btn-secondary btn-sm"
                                            onClick={callWaiter}
                                            data-testid="call-waiter"
                                            style={{ flex: 1, justifyContent: 'center', gap: 6 }}
                                            disabled={!isOpen}>
                                        <Bell size={14} /> {t('callWaiter')}
                                    </button>
                                )}
                                {requestBillEnabled && (
                                    <button className="btn btn-secondary btn-sm"
                                            onClick={() => { setBillSent(false); setOpenBill(true); }}
                                            data-testid="request-bill"
                                            style={{ flex: 1, justifyContent: 'center', gap: 6 }}
                                            disabled={!isOpen || orderPaid}
                                            title={orderPaid ? 'Comanda a fost deja plătită' : undefined}>
                                        <Banknote size={14} /> {orderPaid ? 'Plătită ✓' : t('requestBill')}
                                    </button>
                                )}
                            </div>
                        );
                    })()}
                    {/* Row 2 — primary action: open cart (full-width, prominent) */}
                    {orderingEnabled && (
                        <button className="btn btn-primary"
                                style={{ width: '100%', justifyContent: 'center', gap: 8, padding: '0.85rem' }}
                                onClick={() => setOpenCart(true)}
                                disabled={!isOpen || !cart.length}
                                data-testid="open-cart">
                            <ShoppingCart size={18} />
                            {cart.length > 0
                                ? t('cartFilled', { count: cart.length, total: total.toFixed(2), currency })
                                : t('cartEmpty')}
                        </button>
                    )}
                </div>
            )}

            {openBill && (
                <div className="modal-backdrop" onClick={() => setOpenBill(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="bill-modal">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h2 className="headline" style={{ fontSize: '1.6rem', margin: 0 }}>{t('billTitle')}</h2>
                            <button className="btn btn-ghost btn-sm" onClick={() => setOpenBill(false)}><X size={16} /></button>
                        </div>
                        <p style={{ color: 'var(--text-muted)', marginTop: 0, marginBottom: '1.2rem', fontSize: '0.92rem' }}>
                            {t('billHint')}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <button type="button" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12, padding: '1rem 1.1rem' }}
                                    onClick={() => requestBill('cash')} data-testid="bill-cash">
                                <Banknote size={20} color="var(--amber)" />
                                <span>
                                    <strong style={{ display: 'block', color: '#fff' }}>{t('cash')}</strong>
                                    <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{t('cashHint')}</span>
                                </span>
                            </button>
                            <button type="button" className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: 12, padding: '1rem 1.1rem' }}
                                    onClick={() => requestBill('card')} data-testid="bill-card">
                                <CreditCard size={20} color="var(--neon-cyan)" />
                                <span>
                                    <strong style={{ display: 'block', color: '#fff' }}>{t('card')}</strong>
                                    <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{t('cardHint')}</span>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {billSent && (
                <div className="modal-backdrop" onClick={() => setBillSent(false)}>
                    <div className="modal" style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()} data-testid="bill-sent">
                        <div style={{ width: 56, height: 56, borderRadius: 999, background: 'rgba(245,158,11,0.12)', border: '1px solid var(--amber)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                            <Receipt size={26} color="var(--amber)" />
                        </div>
                        <h2 className="headline" style={{ fontSize: '1.5rem', margin: 0 }}>{t('billSentTitle')}</h2>
                        <p style={{ color: 'var(--text-muted)' }}>{t('billSentHint')}</p>
                        <button className="btn btn-primary" onClick={() => setBillSent(false)}>{t('ok')}</button>
                    </div>
                </div>
            )}

            {openProduct && (
                <div className="modal-backdrop" onClick={() => setOpenProduct(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        {openProduct.image_url && (
                            <div style={{ margin: '-1.5rem -1.5rem 1rem', borderRadius: '1.25rem 1.25rem 0 0', overflow: 'hidden' }}>
                                <img src={openProduct.image_url} alt={openProduct.name} style={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }} />
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                            <div>
                                <h2 className="headline" style={{ fontSize: '1.6rem', margin: 0 }}>{openProduct.name}</h2>
                                <ProductDescription description={openProduct.description} label={t('description')} />
                                <ProductIngredients ingredients={openProduct.ingredients} label={t('ingredients')} />
                                <ProductNutrition nutrition={openProduct.nutrition} variantWeight={openProduct.variants[0]?.weight} />
                                <ProductTags tags={openProduct.tags} label={t('tags')} />
                                <ProductAllergens allergens={openProduct.allergens} label={t('allergens')} />
                            </div>
                            <button className="btn btn-ghost btn-sm" onClick={() => setOpenProduct(null)}><X size={16} /></button>
                        </div>
                        {orderingEnabled ? (
                            <>
                                <div className="kicker" style={{ marginTop: 16, marginBottom: 8 }}>
                                    {openProduct.variants.length > 1 ? t('chooseVariant') : t('addToCart')}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {openProduct.variants.length === 1 ? (
                                        <button
                                            type="button"
                                            onClick={() => addToCart(openProduct, openProduct.variants[0])}
                                            className="btn btn-primary"
                                            style={{ justifyContent: 'space-between' }}
                                            data-testid={`variant-${openProduct.variants[0].id}`}
                                        >
                                            <span>
                                                {[
                                                    openProduct.variants[0].weight,
                                                    fmtCal(openProduct.variants[0].calories),
                                                    fmtCalPortion(openProduct.variants[0].calories_per_portion),
                                                ].filter(Boolean).join(' · ') || t('addProduct')}
                                            </span>
                                            <span style={{ fontWeight: 600 }}>{openProduct.variants[0].price} {currency}</span>
                                        </button>
                                    ) : openProduct.variants.map((v) => (
                                        <button key={v.id} onClick={() => addToCart(openProduct, v)} className="btn btn-secondary" style={{ justifyContent: 'space-between' }} data-testid={`variant-${v.id}`}>
                                            <span>
                                                {v.name}
                                                {(v.weight || v.calories || v.calories_per_portion) && ` · ${[v.weight, fmtCal(v.calories), fmtCalPortion(v.calories_per_portion)].filter(Boolean).join(' · ')}`}
                                            </span>
                                            <span style={{ color: 'var(--amber)', fontWeight: 600 }}>{v.price} {currency}</span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                                {openProduct.variants.map((v) => (
                                    <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.7rem 0.9rem', background: 'var(--surface-2)', borderRadius: 10 }}>
                                        <span>
                                            {openProduct.variants.length > 1 ? v.name : ''}
                                            {(v.weight || v.calories || v.calories_per_portion) && `${openProduct.variants.length > 1 ? ' · ' : ''}${[v.weight, fmtCal(v.calories), fmtCalPortion(v.calories_per_portion)].filter(Boolean).join(' · ')}`}
                                        </span>
                                        <span style={{ color: 'var(--amber)', fontWeight: 600 }}>{v.price} {currency}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <MenuFiltersModal
                open={openFilters}
                onClose={() => setOpenFilters(false)}
                hiddenAllergens={hiddenAllergens}
                requiredDiets={requiredDiets}
                onChange={updateFilters}
                hiddenCount={hiddenInCategory}
                totalCount={totalProductsInCategory}
            />

            {openInfo && (
                <ClientRestaurantInfoModal
                    open={openInfo}
                    onClose={() => setOpenInfo(false)}
                    t={t}
                    clientInfo={restaurant.client_info}
                    schedulePills={data.schedule_pills || []}
                    enabledLangs={enabledLangs}
                    lang={lang}
                    onChangeLang={changeLang}
                />
            )}

            {openCart && (
                <div className="modal-backdrop" onClick={() => setOpenCart(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h2 className="headline" style={{ fontSize: '1.6rem', margin: 0 }}>{t('cartTitle')}</h2>
                            <button className="btn btn-ghost btn-sm" onClick={() => setOpenCart(false)}><X size={16} /></button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                            {cart.map((it, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0.7rem', background: 'var(--surface-2)', borderRadius: 10 }}>
                                    <div style={{ flex: 1, marginRight: 8 }}>
                                        <div style={{ fontWeight: 500, fontSize: '0.95rem' }}>{it.product_name}</div>
                                        <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{it.variant_name} · {it.price} {currency}</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <button className="btn btn-ghost btn-sm" onClick={() => changeQty(i, -1)}><Minus size={13} /></button>
                                        <span style={{ minWidth: 22, textAlign: 'center' }}>{it.quantity}</span>
                                        <button className="btn btn-ghost btn-sm" onClick={() => changeQty(i, 1)}><Plus size={13} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                            <span className="kicker">{t('total')}</span>
                            <span className="headline" style={{ fontSize: '1.8rem', color: 'var(--amber)' }}>{total.toFixed(2)} {currency}</span>
                        </div>
                        <button className="btn btn-primary" style={{ width: '100%' }} onClick={sendOrder} disabled={!cart.length} data-testid="send-order">
                            <Check size={16} /> {activeOrder ? t('addToActiveOrder') : t('sendOrder')}
                        </button>
                    </div>
                </div>
            )}

            {submitted && (
                <div className="modal-backdrop" onClick={() => setSubmitted(null)}>
                    <div className="modal" style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()} data-testid="order-submitted">
                        <div style={{ width: 60, height: 60, borderRadius: 999, background: 'rgba(20,241,149,0.15)', border: '1px solid var(--neon-teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                            <Check size={28} color="var(--neon-teal)" />
                        </div>
                        <h2 className="headline" style={{ fontSize: '1.8rem', margin: 0 }}>{t('orderSentTitle')}</h2>
                        <p style={{ color: 'var(--text-muted)' }}>{t('orderSentHint', { id: submitted.id.slice(0, 6) })}</p>
                        <button className="btn btn-primary" onClick={() => setSubmitted(null)}>{t('thanks')}</button>
                    </div>
                </div>
            )}
        </div>
    );
}
